/**
 * /product-demo — investor-facing borrower-journey video showcase.
 *
 * Standalone page: no app sidebar, no top nav, no borrower dashboard
 * chrome. Openly public (Jesse, 2026-10-21 — "leave public"): no password gate.
 * The videos were always downloadable by URL from /public/videos anyway.
 *
 * Static video assets live in /public/videos/*.mp4 (served directly by
 * Vercel — NOT bundled by webpack). The VideoBlock component handles
 * the case where a file isn't on disk yet (404 on load) with a styled
 * "Coming soon" placeholder so the page doesn't show a broken player.
 *
 * Route is registered non-lazy in App.js to match the convention of
 * every other route in this app (PriceTool, UnderwritingPortal, etc.
 * are all non-lazy). The component itself is tiny + the videos are
 * static assets, so lazy-loading wouldn't materially shrink the main
 * bundle anyway.
 */
import React, { useEffect, useRef, useState } from 'react';

const CHAPTERS = [
  {
    num: '01',
    title: 'Pre-Qualification',
    tabTitle: 'Pre-Qual',
    blurb:
      'See how Doorly screens borrowers before they ever create an account — conversational, jargon-free, and no credit pull required.',
    src: '/videos/doorly-demo-1-prequal.mp4',
  },
  {
    num: '02',
    title: 'Application',
    blurb:
      'A fully digital application that meets borrowers where they are — short, focused sections instead of a wall of form fields.',
    // 🚨 NO VIDEO (Jesse, 2026-10-15): the old recording shows the pre-redesign
    // application. Unlinked, not deleted — frontend/public/videos/doorly-demo-2-application.mp4
    // stays until the 8-step re-recording replaces it.
    src: null,
  },
  {
    num: '03',
    // 🔑 NO ENGINE NAME (Jesse, 2026-10-15): "ReadyCheck" is internal and is
    // never shown to a borrower or partner, marketing included. The step is
    // described by what it does.
    title: 'Document Verification',
    tabTitle: 'Verification',
    blurb:
      'Watch Doorly analyze income, assets, credit, and housing history automatically — in minutes, not weeks.',
    // 🚨 NO VIDEO (Jesse, 2026-10-15): the old recording showed the pre-redesign
    // app and the retired engine name. Unlinked, not deleted — the file stays at
    // frontend/public/videos/doorly-demo-3-verification.mp4 until the new 8-step
    // recording replaces it. A chapter with no src renders its title and blurb
    // only: no player, no empty frame, no "coming soon".
    src: null,
  },
  {
    num: '04',
    title: 'Approval & SmartEstimate',
    titleNode: (
      <>
        Approval &amp; SmartEstimate<sup style={{ fontSize: '0.5em', marginLeft: 2, fontWeight: 700 }}>™</sup>
      </>
    ),
    tabTitle: 'SmartEstimate',
    blurb:
      "From approval to 'Start Shopping' — the borrower sees their exact loan options and monthly payment before touching a home.",
    src: '/videos/doorly-demo-4-approval.mp4',
  },
  {
    num: '05',
    title: 'Home Shopping',
    blurb:
      'Watch a borrower browse real MLS listings filtered to their approved amount — AI-powered search, live property data, and SmartEstimate payment previews on every card.',
    src: '/videos/doorly-demo-5-shopping.mp4',
  },
  // Chapters 06–10 ship without MP4s on disk today; VideoBlock's
  // onError handler catches the 404 and renders the "Coming soon"
  // placeholder card. Once we record + drop the corresponding files
  // at the listed src paths, the players light up automatically — no
  // code change needed. The directory tabs above pick them up via
  // the same CHAPTERS map.
  {
    num: '06',
    title: 'Start Purchase',
    blurb:
      'When the borrower finds their home, Doorly drafts the offer, coordinates with the listing agent, and tracks acceptance — all in-app.',
    src: '/videos/doorly-demo-6-requestoffer.mp4',
  },
  {
    num: '07',
    title: 'Under Contract',
    blurb:
      'Option period, inspection, appraisal, and seller-credit negotiations — every contract deadline tracked and surfaced before it slips.',
    src: '/videos/doorly-demo-7-contract.mp4',
  },
  {
    num: '08',
    title: 'Closing',
    blurb:
      'Title commitment, hazard insurance, and final document collection — Doorly orchestrates the closing checklist so the borrower never wonders what comes next.',
    src: '/videos/doorly-demo-8-closing.mp4',
  },
];

// VideoBlock — wraps the HTML5 player and traps 404s. The onError event
// fires when the browser can't load the source (file missing, MIME
// wrong, etc.). We flip a local state flag and render a "Coming soon"
// card in place of the broken player, so the page stays clean if a
// chapter's MP4 hasn't been uploaded yet.
function VideoBlock({ src, title }) {
  const [errored, setErrored] = useState(false);

  if (errored) {
    return (
      <div
        style={{
          width: '100%',
          maxWidth: 900,
          margin: '0 auto',
          padding: '48px 24px',
          background: '#f9fafb',
          border: '1px dashed #d1d5db',
          borderRadius: 12,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 36, marginBottom: 8 }}>🎬</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
          Coming soon
        </div>
        <div style={{ fontSize: 13, color: '#6b7280' }}>
          The {title} walkthrough is being finalized — check back shortly.
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 900,
        margin: '0 auto',
        borderRadius: 12,
        overflow: 'hidden',
        background: '#000',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      }}
    >
      <video
        controls
        preload="metadata"
        playsInline
        onError={() => setErrored(true)}
        style={{ display: 'block', width: '100%', height: 'auto' }}
      >
        <source src={src} type="video/mp4" />
        {/* Fallback for browsers that don't understand <source> — the
            onError handler above catches the actual missing-file case. */}
        Your browser does not support embedded video.
      </video>
    </div>
  );
}

function DemoBody() {
  // Chapter directory — sticky tab bar below the header lets investors
  // jump straight to any chapter without scrolling. As more chapters
  // get added the bar grows horizontally; on narrow screens it scrolls
  // its own row.
  //
  // Active-tab highlight comes from an IntersectionObserver watching
  // each section: whichever is currently intersecting the viewport
  // (using a top offset so it doesn't activate until the header +
  // tab bar are clear of it) sets activeChapter. Plain scrollIntoView
  // on click; sections carry scroll-margin-top so the sticky chrome
  // doesn't cover the heading after the jump.
  const sectionRefs = useRef({});
  const [activeChapter, setActiveChapter] = useState(CHAPTERS[0]?.num || null);

  // Directory scroll-state — drives the fade gradient + chevron
  // overlays that show on mobile when the chapter bar can scroll
  // further in either direction. The bar overflows on narrow
  // viewports (10+ chapters past ~600px); without a visual hint the
  // tabs to the right of the fold are invisible. canLeft/canRight
  // flip via a scroll listener so each gradient only shows when
  // there's actually content in that direction.
  const navScrollRef = useRef(null);
  const [navScroll, setNavScroll] = useState({ canLeft: false, canRight: false });

  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const update = () => {
      setNavScroll({
        canLeft:  el.scrollLeft > 4,
        canRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost intersecting section. IntersectionObserver
        // can fire with multiple visible sections at once during a
        // smooth scroll; sort by bounding-rect top so we always
        // promote the highest visible.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const num = visible[0].target.dataset.chapterNum;
          if (num) setActiveChapter(num);
        }
      },
      // rootMargin tightens the activation window so a chapter only
      // counts as "active" once it's roughly in the upper half of the
      // viewport (offsets the sticky header + tab bar height).
      { rootMargin: '-120px 0px -55% 0px', threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((node) => {
      if (node) observer.observe(node);
    });
    return () => observer.disconnect();
  }, []);

  const jumpToChapter = (num) => {
    const node = sectionRefs.current[num];
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff', color: '#111827', fontFamily: 'inherit' }}>
      {/* Sticky minimal header — just the logo + Confidential pill.
          No app nav links, no login button, no sidebar. */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid #f3f4f6',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <img
          src="/doorly_logo_black.png"
          alt="Doorly"
          style={{ height: 32, width: 'auto', display: 'block' }}
        />
        <span
          style={{
            display: 'inline-block',
            padding: '5px 12px',
            borderRadius: 999,
            background: '#3a6ad9',
            color: '#ffffff',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
          }}
        >
          Confidential
        </span>
      </header>

      {/* Hero */}
      <section
        style={{
          maxWidth: 900,
          margin: '0 auto',
          padding: '72px 24px 56px',
          textAlign: 'center',
        }}
      >
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '999px',
          // Symmetric horizontal padding so the yellow Demo pill and
          // the "Product" text sit equidistant from the outer pill's
          // edges. The prior 4px/12px/4px/8px asymmetric padding left
          // a noticeably larger gap on the right side because the
          // yellow inner pill's own 2px/10px padding made its
          // bounding box bigger than the bare "Product" text on the
          // left.
          padding: '4px 8px',
          marginBottom: '20px',
        }}>
          <span style={{ color: '#111827', fontSize: '13px', fontWeight: 500 }}>Product</span>
          <span style={{
            background: '#fffd5b',
            color: '#111827',
            fontWeight: 700,
            fontSize: '13px',
            padding: '2px 10px',
            borderRadius: '999px',
          }}>Demo</span>
        </div>
        <h1
          style={{
            margin: 0,
            fontSize: 'clamp(32px, 5vw, 48px)',
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: -0.5,
          }}
        >
          <span style={{ color: '#111827' }}>The Doorly </span>
          <span style={{ color: '#3a6ad9' }}>Borrower Journey</span>
        </h1>
        <p
          style={{
            margin: '20px auto 0',
            maxWidth: 720,
            fontSize: 'clamp(15px, 1.4vw, 17px)',
            color: '#6b7280',
            lineHeight: 1.6,
          }}
        >
          Watch the complete end-to-end experience — from very first question to closing on a home.
          Doorly is the first fully digital, app-to-keys platform built for the borrowers traditional
          lenders leave behind.
        </p>
      </section>

      {/* Chapter directory — sits below the hero so the page opens with
          headline copy front-and-center, then becomes a sticky chapter
          navigator the moment the viewer scrolls past it. Click a tab
          to smooth-scroll to that chapter; the active tab tracks the
          currently-visible section via IntersectionObserver. Horizontal
          scroll on narrow screens so the bar never wraps.
          Mobile scroll affordance: a fade-gradient + chevron overlay
          on each side of the scrollable area appears only when there
          are more tabs to scroll to in that direction (driven by
          navScroll state above). Without it, the off-screen tabs were
          invisible on narrow viewports — the user couldn't tell more
          chapters existed past the right edge. */}
      <div
        style={{
          // position: sticky lifts the whole bar (nav + gradient
          // overlays) into the sticky context; the inner overlays use
          // position: absolute relative to this container.
          position: 'sticky',
          top: 60,
          zIndex: 9,
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(8px)',
          borderTop: '1px solid #f3f4f6',
          borderBottom: '1px solid #f3f4f6',
        }}
      >
        <div style={{ position: 'relative' }}>
        <nav
          ref={navScrollRef}
          aria-label="Chapter directory"
          style={{
            padding: '10px 16px',
            overflowX: 'auto',
          }}
        >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 6,
            minWidth: 'max-content',
            margin: '0 auto',
          }}
        >
          {CHAPTERS.map((chapter) => {
            const active = activeChapter === chapter.num;
            return (
              <button
                key={chapter.num}
                type="button"
                onClick={() => jumpToChapter(chapter.num)}
                title={`Chapter ${chapter.num} · ${chapter.title}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '5px 11px',
                  borderRadius: 999,
                  border: 'none',
                  cursor: 'pointer',
                  background: active ? '#3a6ad9' : '#f3f4f6',
                  color: active ? '#ffffff' : '#374151',
                  fontSize: 12,
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  transition: 'background-color 0.15s ease, color 0.15s ease',
                }}
              >
                {chapter.tabTitle || chapter.title}
              </button>
            );
          })}
        </div>
        </nav>

        {/* Left fade — only when scrolled past the start. Acts as a
            visual handle that there's content the other way; pointer
            events disabled so the gradient never blocks tab clicks. */}
        {navScroll.canLeft && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0, bottom: 0, left: 0,
              width: 36,
              pointerEvents: 'none',
              background:
                'linear-gradient(to right, rgba(255,255,255,0.98) 30%, rgba(255,255,255,0) 100%)',
            }}
          />
        )}
        {/* Right fade + chevron — appears whenever more tabs sit past
            the right edge. Chevron is the explicit "scroll for more"
            affordance; the fade is the subliminal one. */}
        {navScroll.canRight && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0, bottom: 0, right: 0,
              width: 48,
              pointerEvents: 'none',
              background:
                'linear-gradient(to left, rgba(255,255,255,0.98) 30%, rgba(255,255,255,0) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              paddingRight: 6,
            }}
          >
            <span
              style={{
                fontSize: 14,
                color: '#6b7280',
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              ›
            </span>
          </div>
        )}
        </div>
      </div>

      {/* Chapter sections */}
      <main style={{ padding: '0 24px 64px' }}>
        {CHAPTERS.map((chapter, i) => (
          <section
            key={chapter.num}
            ref={(node) => { sectionRefs.current[chapter.num] = node; }}
            data-chapter-num={chapter.num}
            id={`chapter-${chapter.num}`}
            style={{
              maxWidth: 1100,
              margin: '0 auto',
              padding: '48px 0',
              borderTop: i === 0 ? 'none' : '1px solid #f3f4f6',
              // Pushes the section heading below the sticky header
              // (~60px) + sticky directory bar (~50px) when scrollIntoView
              // lands here from a tab click, so the chapter title stays
              // visible instead of getting tucked under the chrome.
              scrollMarginTop: 120,
            }}
          >
            <div style={{ maxWidth: 900, margin: '0 auto 24px' }}>
              <div
                style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  borderRadius: 999,
                  background: '#eff3fe',
                  color: '#3a6ad9',
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  marginBottom: 12,
                }}
              >
                {chapter.num}
              </div>
              <h2
                style={{
                  margin: '0 0 10px',
                  fontSize: 'clamp(24px, 3vw, 32px)',
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: -0.3,
                }}
              >
                {chapter.titleNode || chapter.title}
              </h2>
              <p style={{ margin: 0, fontSize: 15, color: '#6b7280', lineHeight: 1.6, maxWidth: 720 }}>
                {chapter.blurb}
              </p>
            </div>
            {chapter.src ? <VideoBlock src={chapter.src} title={chapter.title} /> : null}
          </section>
        ))}
      </main>

      {/* Footer */}
      <footer
        style={{
          borderTop: '1px solid #f3f4f6',
          padding: '32px 24px',
          textAlign: 'center',
          fontSize: 12,
          color: '#6b7280',
        }}
      >
        © {new Date().getFullYear()} Go Doorly, LLC · NMLS #2764749 · Confidential — For Internal Use Only
      </footer>
    </div>
  );
}

export default function ProductDemo() {
  return <DemoBody />;
}
